/**
 * Tax as the till sees it — docs/tax-money-path.md §6.
 *
 * The store that applies to a sale is the SELLER's (merchant of record for
 * the stylist in "Selling as"), not the logged-in user's. This module fetches
 * it once per stylist per session and turns a cart into the same per-rate
 * breakdown the server freezes onto the booking, so what the owner sees in
 * the cart is what lands on the receipt and in the VAT report.
 *
 * Inclusive: the lines are informational ("Includes VAT 20%") — the balance
 * already contains the tax. Exclusive: the lines ADD to the balance ("VAT 20%
 * +£x"); the till stores the total in state.taxAmount so the client is charged
 * net + tax, and the server uplifts the stored prices to the same gross.
 */
import { useEffect, useState } from 'react';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import { inclusiveTaxBreakdown, exclusiveTaxBreakdown } from './taxStore';

const cache = new Map(); // hairdresserUserId -> { at, store }
const TTL_MS = 5 * 60 * 1000;

export const fetchSellerTaxStore = async (hairdresserUserId) => {
  if (!hairdresserUserId) return null;
  const key = String(hairdresserUserId);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.store;
  try {
    const store = await marketingBackend.taxForSeller(key);
    cache.set(key, { at: Date.now(), store });
    return store;
  } catch (e) {
    // Fail closed: no lines rather than wrong lines.
    return null;
  }
};

/** Drop the cache (after the Taxes page saves), so the till re-reads. */
export const invalidateSellerTaxStore = () => cache.clear();

export const useSellerTaxStore = (hairdresserUserId) => {
  const [store, setStore] = useState(null);
  useEffect(() => {
    let alive = true;
    setStore(null);
    fetchSellerTaxStore(hairdresserUserId).then((s) => { if (alive) setStore(s); });
    return () => { alive = false; };
  }, [hairdresserUserId]);
  return store;
};

// Mirrors taxService.effectiveFromStore EXACTLY: an override naming only
// rates that exist at another location falls back to this site's defaults
// (an explicit [] still means no tax).
const effectiveTaxes = (store, domain, itemId) => {
  const enabled = (store.taxes || []).filter((t) => t.enabled !== false);
  const ov = itemId ? (store.overrides || {})[`${domain}:${itemId}`] : null;
  if (ov) {
    const other = new Set((store.otherSiteRateIds || []).map(String));
    const ids = (ov.taxIds || []).map(String);
    const here = ids.filter((id) => !other.has(id));
    if (!(ids.length > 0 && here.length === 0)) {
      const allowed = new Set(here);
      return enabled.filter((t) => allowed.has(String(t.id)));
    }
  }
  return enabled.filter((t) => t.appliesTo && t.appliesTo[domain]);
};

const DOMAIN_OF = { service: 'services', product: 'products', voucher: 'vouchers', shipping: 'shipping' };
const idOf = (item) => item.serviceId || item.productId || item.voucherId || item.catalogId || item.id || null;

/**
 * items: [{ type: 'service'|'product'|'voucher', serviceId|productId|voucherId, price, quantity }]
 * discountFactor: (subtotal − discount) / subtotal, so the lines reflect what
 * the client actually pays — the same base the server snapshots.
 * Returns { rows: [{ label, amount }], total } in pounds, or null when the
 * seller's taxes are off, mode is not inclusive, or nothing is taxed.
 */
export const cartTaxBreakdown = (store, items, discountFactor = 1, priceOf = null) => {
  if (!store || store.enabled !== true) return null;
  const mode = store.calculationMode === 'exclusive' ? 'exclusive' : 'inclusive';
  const byRate = {};
  let total = 0;
  let applied = false;
  for (const item of items || []) {
    const domain = DOMAIN_OF[item.type];
    if (!domain) continue;
    // POS lines carry `price`; diary lines carry `originalPrice` + per-item
    // discounts, so the caller can pass its own line-total function.
    const lineTotal = priceOf ? (Number(priceOf(item)) || 0) : (Number(item.price) || 0) * (Number(item.quantity) || 1);
    const gross = lineTotal * (discountFactor || 1);
    const grossP = Math.round(gross * 100);
    if (grossP <= 0) continue;
    // A line may name its taxes explicitly (delivery: the store's shipping.taxRateId).
    const taxes = Array.isArray(item.taxIds)
      ? (store.taxes || []).filter((t) => t.enabled !== false && item.taxIds.map(String).includes(String(t.id)))
      : effectiveTaxes(store, domain, idOf(item));
    if (taxes.length) applied = true;   // a 0% (zero-rated) line still shows its row
    (mode === 'exclusive' ? exclusiveTaxBreakdown(grossP, taxes) : inclusiveTaxBreakdown(grossP, taxes)).forEach((b) => {
      const label = `${b.name} ${b.rate}%`;
      byRate[label] = (byRate[label] || 0) + b.amountPence;
      total += b.amountPence;
    });
  }
  if (!applied) return null;
  return {
    mode,
    rows: Object.keys(byRate).map((label) => ({ label, amount: byRate[label] / 100 })),
    total: total / 100,
  };
};
